import AddEditCaseTemplateDialog from "@/components/Forms/SubForms/AddEditCaseTemplateDialog.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useDeleteCaseTemplate } from "@/mutations/useDeleteCaseTemplate.tsx";
import { type MrtCaseTemplate, useFetchMrtTemplates } from "@/queries/useFetchMrtCaseTemplates.ts";
import { getErrorMessage } from "@/utils/api.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Stack, TextField } from "@mui/material";
import {
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_Row,
    type MRT_SortingState,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";

const MrtCaseTemplatesTable = () => {
    const { hasPermission } = useEmployee();
    const deleteCaseTemplate = useDeleteCaseTemplate();
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [editCaseTemplateId, setEditCaseTemplateId] = useState<string | undefined>();
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });

    const { data, isError, isRefetching, isLoading } = useFetchMrtTemplates({
        columnFilters,
        globalFilter,
        pagination,
        sorting,
    });

    const handleRowClick = (row: MRT_Row<MrtCaseTemplate>) => {
        if (hasPermission("edit:case-template")) {
            setEditCaseTemplateId(row.original.idUUID);
        }
    };

    const handleDeleteRow = (row: MRT_Row<MrtCaseTemplate>) => {
        if (window.confirm("Are you sure you want to delete this CaseTemplate?")) {
            deleteCaseTemplate.mutate(
                {
                    caseTemplateUUID: row.original.idUUID,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("CaseTemplate removed.", { variant: "success" });
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: CaseTemplate could not be removed.", {
                            variant: "error",
                        });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        }
    };

    const columns = useMemo<MRT_ColumnDef<MrtCaseTemplate>[]>(() => {
        const columnDefs: MRT_ColumnDef<MrtCaseTemplate>[] = [
            {
                accessorKey: "name",
                header: "Name",
            },
            {
                accessorKey: "description",
                header: "Description",
            },
        ];

        if (hasPermission("delete:case-template")) {
            columnDefs.push({
                accessorKey: "delete",
                header: "Delete",
                size: 50,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            handleDeleteRow(row);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                ),
            });
        }

        return columnDefs;
    }, [hasPermission]);

    const table = useMaterialReactTable({
        columns,
        data: isLoading || isRefetching || isError || data === undefined ? [] : data,

        initialState: {
            showColumnFilters: false,
            showGlobalFilter: true,
            density: "compact",
        },

        // top toolbar
        enableTopToolbar: true,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableColumnFilters: false,

        // server side searching and pagination
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,

        // state
        // rowCount: data?.meta.totalRowCount ?? 0,
        rowCount: 0,
        state: {
            columnFilters,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            showLoadingOverlay: isLoading || isRefetching,
            sorting,
        },

        renderTopToolbar: ({ table }) => (
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ padding: "0.5rem" }}
            >
                <TextField
                    placeholder="Search..."
                    value={table.getState().globalFilter || ""}
                    onChange={(e) => table.setGlobalFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                />

                <AddEditCaseTemplateDialog
                    showAddButton={true}
                    editCaseTemplateId={editCaseTemplateId}
                    clearEditCaseTemplateId={() => {
                        setEditCaseTemplateId(undefined);
                    }}
                />
            </Stack>
        ),

        // design
        muiToolbarAlertBannerProps: isError
            ? {
                  color: "error",
                  children: "Error loading data",
              }
            : undefined,
        muiTableBodyRowProps: ({ row }: { row: MRT_Row<MrtCaseTemplate> }) => ({
            onClick: () => handleRowClick(row),
            sx: { cursor: "pointer" },
        }),
    });

    return <MaterialReactTable table={table} />;
};

export default MrtCaseTemplatesTable;
